import { TabBar, TabBarItem } from 'react-native-tab-view';
import { Colors, Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import { isNotNil, isWeb } from 'shared/src/utils';
import { TabSlotText } from './TabSlotText';
import { TabOverlayImage } from './TabOverlayImage';
import type { HomeTabRoute } from '../HomeContents';
import type {
  SceneRendererProps,
  NavigationState,
} from 'react-native-tab-view';

type HomeTabBarProps = SceneRendererProps & {
  navigationState: NavigationState<HomeTabRoute>;
};

export const HomeTabBar = (props: HomeTabBarProps) => {
  const { navigationState } = props;
  const currentRoute = navigationState.routes[navigationState.index];
  const { width: screenWidth } = useScreenSize();

  return (
    <Flex alignItems={'center'}>
      <TabBar
        {...props}
        style={{
          height: 44,
          backgroundColor: Colors.bg_always_white,
          borderBottomWidth: 1,
          borderBottomColor: Colors.border_tertiary,
          elevation: 0,
          shadowOpacity: 0,
          width: screenWidth,
        }}
        tabStyle={{
          width: 'auto',
          paddingHorizontal: 2,
        }}
        scrollEnabled={true}
        gap={16}
        contentContainerStyle={{
          paddingHorizontal: 16,
        }}
        renderTabBarItem={tabItemProps => {
          const render = tabItemProps.route.render;
          const isFocused =
            tabItemProps.navigationState.index ===
            tabItemProps.navigationState.routes.indexOf(tabItemProps.route);

          const overlayImage = render?.overlay_image;
          if (isNotNil(overlayImage)) {
            const source = isFocused
              ? overlayImage.selected_source
              : overlayImage.unselected_source;
            const imageUrl = source?.url;
            const imageWidth = overlayImage.width ?? 48;
            const imageHeight = overlayImage.height ?? 44;

            if (isNotNil(imageUrl)) {
              return (
                <TabBarItem
                  {...tabItemProps}
                  key={tabItemProps.route.key}
                  label={() => (
                    <TabOverlayImage
                      uri={imageUrl}
                      width={imageWidth}
                      height={imageHeight}
                      animation={source?.animation ?? null}
                    />
                  )}
                  badge={
                    render?.is_ad
                      ? () => (
                          <Flex position={'absolute'} top={4} right={-8}>
                            <Typography type="subtitle7" rawColor="#999">
                              AD
                            </Typography>
                          </Flex>
                        )
                      : undefined
                  }
                />
              );
            }
          }

          const activeColor =
            render?.selected_text_color ?? Colors.content_primary;
          const inactiveColor = render?.text_color ?? Colors.content_secondary;

          const overlayTextUnselected =
            render?.overlay_text?.unselected ?? null;

          return (
            <Flex position={'relative'}>
              {isNotNil(overlayTextUnselected) && !isFocused && (
                <Flex
                  position={'absolute'}
                  top={2}
                  left={0}
                  right={0}
                  alignItems={'center'}
                  zIndex={1}
                >
                  <TabSlotText overlayText={overlayTextUnselected} />
                </Flex>
              )}
              <TabBarItem
                {...tabItemProps}
                key={tabItemProps.route.key}
                activeColor={activeColor}
                inactiveColor={inactiveColor}
                label={({ route, focused, color }) => (
                  <Typography
                    numberOfLines={1}
                    type={focused ? 'subtitle1.5' : 'body1.5'}
                    rawColor={color}
                  >
                    {route.name}
                  </Typography>
                )}
                badge={
                  render?.is_ad
                    ? () => (
                        <Flex position={'absolute'} top={4} right={-8}>
                          <Typography type="subtitle7" rawColor="#999">
                            AD
                          </Typography>
                        </Flex>
                      )
                    : undefined
                }
              />
            </Flex>
          );
        }}
        indicatorStyle={[
          {
            height: 2,
            backgroundColor:
              currentRoute?.render?.selected_text_color ??
              Colors.content_primary,
            // transform: [{ translateX: isWeb ? 16 : 0 }],
          },
          isWeb ? { transform: [{ translateX: 16 }] } : {},
        ]}
      />
    </Flex>
  );
};
